import React, { ReactNode } from 'react';
// Models
import IOption from 'app/models/Option';
// Mui
import {
  TextField, TextFieldProps, SelectProps,
  MenuItem, InputAdornment,
  Tooltip
} from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
//
import IconButton from './IconButton';
// Utilities
import { generateAttrForTesting } from 'app/utilities/Utilities';

type Props = TextFieldProps & {
  options: IOption[] | null;
  onClear?: () => void;
  tooltip?: string;
  renderOptions?: (option:IOption, index:number) => ReactNode;
}

const Select = React.forwardRef<any, Props>(({
  options, onClear, tooltip, renderOptions = (option:IOption, index:number) => (
    <MenuItem
      key={`option-item-${index}-${option.id}`}
      value={option.id}
    >{option.name}</MenuItem>
  ), ...restProps
}, ref) => {
  const isMultiple = (restProps.slotProps?.select as SelectProps)?.multiple;

  const renderValue = (value:any) => {
    if (value === null || value === undefined || value === '') return '';

    if ( isMultiple && Array.isArray(value) ) {
      return value.map(v => {
        const foundedOption = options?.find(option => option.id.toString() === v.toString());
        return foundedOption?.name || '';
      }).join(', ');
    }

    const foundedOption = options?.find(option => option.id.toString() === value.toString());
    if (tooltip) return (
      <Tooltip title={tooltip}>
        <span>{foundedOption?.name || ''}</span>
      </Tooltip>
    )
    return foundedOption?.name || '';
  }

  const slotProps:TextFieldProps['slotProps'] = {
    select: {
      ...generateAttrForTesting('control', 'select', restProps.name),
      ...restProps.slotProps?.select,
      MenuProps: {
        transformOrigin: { horizontal: 'right', vertical: 'top' },
        anchorOrigin: { horizontal: 'right', vertical: 'bottom' }
      },
      renderValue
    }
  }

  if ( onClear ) {
    const hasValue = restProps.value && (Array.isArray(restProps.value) ? restProps.value.length > 0 : restProps.value !== '');
    slotProps['input'] = {
      ...slotProps['input'],
      endAdornment: hasValue ? (
        <InputAdornment position="end">
          <IconButton
            sx={{ mr: 3 }}
            name="Clear"
            aria-label="Clear"
            onClick={onClear}
            size="small"
          ><ClearIcon fontSize="small" /></IconButton>
        </InputAdornment>
      ) : null
    };
  }

  if ( !options ) return (
    <TextField
      {...restProps}
      ref={ref}
      disabled={true}
      slotProps={slotProps}
    />
  );
  return (
    <TextField
      {...restProps}
      select
      ref={ref}
      disabled={restProps.disabled || !options}
      slotProps={slotProps}
    >{(options || []).map((option:IOption, index:number) => renderOptions(option, index))}</TextField>
  );
});

export default Select;
